import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Star, Heart, Send, Trophy } from "lucide-react";

const BADGE_EMOJIS = {
  "Teamwork": "🤝", "Innovation": "💡", "Leadership": "🌟",
  "Excellence": "🏆", "Dedication": "💪", "Helping Hand": "🙌",
};

function PostCard({ post }) {
  const { t, i18n } = useTranslation();
  const [liked, setLiked] = useState(false);
  const [reactionCount, setReactionCount] = useState(post.reactions_count || 0);

  const handleLike = async () => {
    try {
      await apiClient.post(`v1/recognition/posts/${post.id}/react`, { reaction_type: "like" });
      setLiked(l => !l);
      setReactionCount(c => liked ? c - 1 : c + 1);
    } catch {}
  };

  return (
    <div className="bg-card rounded-xl border p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {post.author?.first_name?.[0]}{post.author?.last_name?.[0]}
          </div>
          <div>
            <div className="text-sm font-medium">
              {post.author?.first_name} {post.author?.last_name}
              <span className="text-muted-foreground font-normal"> {t("portal.recognition.praised")} </span>
              {post.recipient?.first_name} {post.recipient?.last_name}
            </div>
            <div className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString(i18n.language)}</div>
          </div>
        </div>
        {post.badge && (
          <div className="flex items-center gap-1 bg-yellow-50 border border-yellow-200 rounded-full px-2.5 py-1 text-xs font-medium text-yellow-700">
            <span>{BADGE_EMOJIS[post.badge.name] || "🏅"}</span>
            {post.badge.name}
          </div>
        )}
      </div>

      <p className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2">{post.message}</p>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${liked ? "text-red-500" : "text-muted-foreground hover:text-red-400"}`}
        >
          <Heart className={`h-4 w-4 ${liked ? "fill-red-500" : ""}`} />
          {reactionCount > 0 && reactionCount}
        </button>
      </div>
    </div>
  );
}

export default function MaReconnaissance() {
  const { t } = useTranslation();
  const [feed, setFeed] = useState([]);
  const [badges, setBadges] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ recipient_employee_id: "", badge_id: "none", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [f, b, e] = await Promise.all([
        apiClient.get("v1/recognition/feed?limit=20").catch(() => ({ data: [] })),
        apiClient.get("v1/recognition/badges").catch(() => ({ data: [] })),
        apiClient.get("v1/employees/").catch(() => ({ data: [] })),
      ]);
      setFeed(f.data?.items || (Array.isArray(f.data) ? f.data : []));
      setBadges(Array.isArray(b.data) ? b.data : []);
      setEmployees(e.data?.employees || (Array.isArray(e.data) ? e.data : []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSend = async () => {
    if (!form.recipient_employee_id || !form.message) return;
    setSending(true);
    try {
      await apiClient.post("v1/recognition/feed", {
        recipient_id: parseInt(form.recipient_employee_id),
        badge_id: form.badge_id && form.badge_id !== "none" ? parseInt(form.badge_id) : null,
        message: form.message,
        is_public: true,
      });
      setSent(true);
      setForm({ recipient_employee_id: "", badge_id: "none", message: "" });
      setTimeout(() => { setSent(false); setShowForm(false); }, 2000);
      load();
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("common.loading")}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t("portal.recognition.intro")}
        </p>
        <Button size="sm" onClick={() => setShowForm(s => !s)}>
          <Send className="h-4 w-4 mr-1" /> {t("portal.recognition.sendKudos")}
        </Button>
      </div>

      {showForm && (
        <div className="bg-card rounded-xl border p-5 space-y-4">
          <h3 className="font-semibold text-sm">{t("portal.recognition.sendKudosTitle")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t("portal.recognition.colleague")}</label>
              <Select value={form.recipient_employee_id} onValueChange={v => setForm(f => ({ ...f, recipient_employee_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("portal.recognition.choose")} /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={String(e.id)}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t("portal.recognition.badgeOptional")}</label>
              <Select value={form.badge_id} onValueChange={v => setForm(f => ({ ...f, badge_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("portal.recognition.noBadge")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("portal.recognition.noBadge")}</SelectItem>
                  {badges.map(b => <SelectItem key={b.id} value={String(b.id)}>{BADGE_EMOJIS[b.name] || "🏅"} {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">{t("portal.recognition.message")}</label>
            <Textarea
              rows={3}
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder={t("portal.recognition.messagePlaceholder")}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSend} disabled={sending || sent || !form.recipient_employee_id || !form.message}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : sent ? t("portal.recognition.sent") : <><Send className="h-4 w-4 mr-1" /> {t("portal.recognition.send")}</>}
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      )}

      {feed.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <Trophy className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p>{t("portal.recognition.emptyFeed")}</p>
          <p className="text-xs mt-1">{t("portal.recognition.emptyFeedHint")}</p>
        </div>
      )}

      <div className="space-y-3">
        {feed.map(post => <PostCard key={post.id} post={post} />)}
      </div>
    </div>
  );
}
